import {Box, Button, Modal, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {Icon} from 'src/common/generics/components/icon/IconBase'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {useCompany} from 'src/contexts/CompanyContext'
import CloseIcon from 'src/images/closeBlueIcon.svg'
import api from 'src/services/api'
import {ListTypeDetectionColorEnum} from 'src/utilities/enums/listTypeDetectionColorEnum'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import Dropdown from '../../../../components/dropdown/Dropdown'
import {DropdownItem} from '../../../../components/dropdown/DropdownProps'
import {Input} from '../../../../components/input/Input'
import useClients from '../../hooks/useClients'

export type CreateListModalProps = {
  isOpen: boolean
  closeModal: () => void
}

function CreateListModal(props: CreateListModalProps) {
  const {isOpen, closeModal} = props
  const [name, setName] = useState('')
  const [listSelected, setListSelected] = useState<DropdownItem>({} as DropdownItem)
  const [companySelected, setCompanySelected] = useState<DropdownItem>({} as DropdownItem)
  const [clientSelected, setClientSelected] = useState<DropdownItem>({} as DropdownItem)
  const [listTypeDetection, setListTypeDetection] = useState<DropdownItem>({} as DropdownItem)

  const {enqueueSnackbar} = useSnackbar()
  const {companies} = useCompany()
  const {clients: clientItems, getClients} = useClients()
  const hasFacialCreatePermission = ValidateUserPermissions('face:create')
  const hasLicensePlateCreatePermission = ValidateUserPermissions('lpr:create')
  const [isLoading, setIsLoading] = useState(false)
  const listTypeItems = [
    {id: 'face', label: 'Faces'},
    {id: 'lpr', label: 'Placas'},
  ]
  const listTypeDetectionItems = [
    {id: 'blacklist', label: 'Bloqueado'},
    {id: 'whitelist', label: 'Permitido'},
  ]
  const companyItems = companies.map((company) => ({id: `${company.id}`, label: company.name}))

  if (!hasFacialCreatePermission) listTypeItems.shift()
  if (!hasLicensePlateCreatePermission) listTypeItems.pop()

  useEffect(() => {
    getClients(+companySelected.id)
  }, [companySelected])

  function clearFields() {
    setName('')
    setListSelected({} as DropdownItem)
    setCompanySelected({} as DropdownItem)
    setClientSelected({} as DropdownItem)
  }

  function handleClose() {
    clearFields()
    closeModal()
  }

  const findColorByDetectionType = (detectionTypeId: string) => {
    if (detectionTypeId === 'blacklist') return ListTypeDetectionColorEnum.BLACKLIST
    else if (detectionTypeId === 'whitelist') return ListTypeDetectionColorEnum.WHITELIST
    return undefined
  }

  async function handleSaveList() {
    try {
      setIsLoading(true)
      const body = {
        name,
        companyId: +companySelected.id,
        clientId: +clientSelected.id || undefined,
        listColor: findColorByDetectionType(listTypeDetection.id),
      }

      if (listSelected.label === 'Faces') await api.facialRecognition.createFacialList(body)
      else await api.lpr.createLprList(body)

      enqueueSnackbar('Lista criada com sucesso', {variant: 'success'})
      handleClose()
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Erro ao criar lista')
    } finally {
      setIsLoading(false)
    }
  }

  const onSelectChangeCompany = (e: DropdownItem) => {
    if (e.id === companySelected.id) return
    setClientSelected({} as DropdownItem)
    setCompanySelected(e)
  }

  return (
    <Modal isOpen={isOpen} closeModal={() => handleClose()} backgroundColor='white'>
      <Box padding='20px' display='flex' direction='column' justifyContent='space-between' height='580px' width='500px'>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='p' weight={500} size='20px' color='#344054'>
            Adicionar Lista
          </Typography>

          <Icon src={CloseIcon} cursor='pointer' onClick={() => handleClose()} style={{width: '12px'}} />
        </Box>

        <Box display='flex' direction='column' margin='20px 0'>
          <Typography variant='span' size='14px' color='#344054'>
            Nova Lista
          </Typography>

          <Typography variant='span' size='12px' color='#98A2B3'>
            Preencha abaixo as informações sobre a lista
          </Typography>
        </Box>

        <Box height='90%' display='flex' direction='column' justifyContent='space-between'>
          <Box display='flex' direction='column' justify='center' align='center' rowGap='40px'>
            <Input.Root
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              labelFontWeight='400'
              label='Nome da lista'
              value={name}
              min={4}
              controlled
              onChange={(e) => setName(e.target.value)}
              maxLength={99}
              required
            />
            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              items={listTypeItems}
              label='Tipo de lista'
              onChangeSelected={(e) => setListSelected(e)}
              required
            />
            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              items={listTypeDetectionItems}
              label='Permissões da lista'
              onChangeSelected={(e) => setListTypeDetection(e)}
              required
            />
            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              selected={companySelected}
              items={companyItems || []}
              label='Empresa'
              onChangeSelected={(e) => onSelectChangeCompany(e)}
              required
            />
            <Dropdown
              labelFontSize='14px'
              height='40px'
              borderColor='#D0D5DD'
              fontSize='12px'
              selected={clientSelected}
              items={companySelected.id ? clientItems : []}
              label='Cliente'
              onChangeSelected={(e) => setClientSelected(e)}
            />
          </Box>
          <Box display='flex' columnGap='10px' justify='flex-end'>
            <Button fontSize='12px' height='38px' width='120px' variant='outlined' color='#8E8E8E' onClick={() => handleClose()}>
              Cancelar
            </Button>
            <Button
              fontSize='12px'
              height='38px'
              width='120px'
              disabledTextColor='#8E8E8E'
              isLoading={isLoading}
              disabled={!name || !listSelected.id || !companySelected.id || !listTypeDetection.id}
              onClick={() => !isLoading && handleSaveList()}>
              Salvar
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default CreateListModal
